import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["selectedContainer", "allContainer", "searchField", "noThreads", "noSelection"]
    static classes = ["selected"]
    static values = {
        id: String,
    }

    selectedThreadIDs = {}
    searchString = ""
    selectedIndex = 0
    visibleCount = 0
    selectedElement = null

    connect() {
        // Initialize selectedThreadIDs from current parents
        this.selectedContainerTarget.querySelectorAll("[data-thread-id]").forEach((el) => {
            this.selectedThreadIDs[el.dataset.threadId] = true
        })

        this.render()
    }

    render() {
        this.renderSelectedThreads()
        this.renderAllThreads()
    }

    renderSelectedThreads() {
        const children = []
        // Keep the "no selection" placeholder as first child
        children.push(this.noSelectionTarget)

        // Clone selected threads from the all container
        const all = this.allContainerTarget.querySelectorAll("[data-thread-id]")
        for (const thread of all) {
            if (this.selectedThreadIDs[thread.dataset.threadId]) {
                const clone = thread.cloneNode(true)
                // Add remove button to clone
                this.makeRemovable(clone)
                children.push(clone)
            }
        }

        // Show/hide "no selection" message
        if (children.length === 1) {
            this.noSelectionTarget.style.display = "block"
        } else {
            this.noSelectionTarget.style.display = "none"
        }

        this.selectedContainerTarget.replaceChildren(...children)
    }

    makeRemovable(element) {
        // Clear existing action
        element.dataset.action = ""
        // Find or create remove button
        let removeBtn = element.querySelector("[data-action*='removeThread']")
        if (!removeBtn) {
            removeBtn = document.createElement("span")
            removeBtn.className = "ml-1 hover:bg-white/20 rounded cursor-pointer"
            removeBtn.innerHTML = '<svg class="icon w-3 h-3"><use xlink:href="/img/icons.svg#lucide--x"></use></svg>'
            element.appendChild(removeBtn)
        }
        removeBtn.dataset.action = "click->thread-picker#removeThread"
    }

    renderAllThreads() {
        let displayedIndex = 0
        Array.from(this.allContainerTarget.children).forEach((child) => {
            // Skip the "no threads" message
            if (child === this.noThreadsTarget) {
                return
            }

            const threadEl = child.querySelector("[data-thread-id]")
            if (!threadEl) {
                return
            }

            const threadName = threadEl.textContent.toLowerCase()
            const query = this.searchString.toLowerCase()
            const matchesSearch = query === "" || threadName.includes(query)

            const id = threadEl.dataset.threadId
            if (this.selectedThreadIDs[id] || !matchesSearch) {
                child.style.display = "none"
                return
            }

            child.style.display = "block"
            if (displayedIndex === this.selectedIndex) {
                this.selectedElement = threadEl
                child.classList.add(this.selectedClass)
            } else {
                child.classList.remove(this.selectedClass)
            }
            displayedIndex++
        })

        this.visibleCount = displayedIndex

        if (displayedIndex === 0) {
            this.noThreadsTarget.style.display = "block"
        } else {
            this.noThreadsTarget.style.display = "none"
        }
    }

    addThread(event) {
        const element = event.target.closest("[data-thread-id]")
        if (element) {
            this.addThreadFromElement(element)
        }
    }

    addThreadFromElement(element) {
        const id = element.dataset.threadId
        if (this.selectedThreadIDs[id]) {
            return
        }
        this.selectedThreadIDs[id] = true
        this.selectedIndex = 0
        this.render()
    }

    removeThread(event) {
        event.stopPropagation()
        const element = event.target.closest("[data-thread-id]")
        if (element) {
            const id = element.dataset.threadId
            delete this.selectedThreadIDs[id]
            this.render()
        }
    }

    save() {
        // For now, just close the modal (no persistence)
        // In the future, this could POST to save the parent relationships
        console.log("Selected parent thread IDs:", Object.keys(this.selectedThreadIDs))
        this.element.closest("dialog").close()
    }

    cancel() {
        this.element.closest("dialog").close()
    }

    search(event) {
        this.searchString = event.target.value
        this.selectedIndex = 0
        this.renderAllThreads()
    }

    enter(event) {
        event.preventDefault()
        if (this.selectedElement) {
            this.addThreadFromElement(this.selectedElement)
        }
    }

    clear() {
        this.searchFieldTarget.value = ""
        this.searchString = ""
        this.selectedIndex = 0
        this.renderAllThreads()
        this.searchFieldTarget.focus()
    }

    selectUp(event) {
        event.preventDefault()
        this.selectedIndex = Math.max(0, this.selectedIndex - 1)
        this.renderAllThreads()
    }

    selectDown(event) {
        event.preventDefault()
        this.selectedIndex = Math.min(this.visibleCount - 1, this.selectedIndex + 1)
        this.renderAllThreads()
    }
}
